import type { ThemeDefinition } from 'vuetify'

export const staticPrimaryColor = '#24ABAF'
export const staticPrimaryDarkenColor = '#1B8083'

export const themes: Record<string, ThemeDefinition> = {
  light: {
    dark: false,
    colors: {
      'primary': staticPrimaryColor,
      'on-primary': '#fff',
      'primary-darken-1': staticPrimaryDarkenColor,
      'secondary': '#8A8D93',
      'secondary-darken-1': '#7C7F84',
      'on-secondary': '#fff',
      'success': '#56CA00',
      'success-darken-1': '#4DB600',
      'on-success': '#fff',
      'info': '#16B1FF',
      'info-darken-1': '#149FE6',
      'on-info': '#fff',
      'warning': '#FFB400',
      'warning-darken-1': '#E6A200',
      'on-warning': '#fff',
      'error': '#FF4C51',
      'error-darken-1': '#E64449',
      'on-error': '#fff',
      'background': '#F4F9FA',
      'on-background': '#2E263D',
      'surface': '#fff',
      'on-surface': '#2E263D',
      'grey-50': '#FAFAFA',
      'grey-100': '#F5F5F5',
      'grey-200': '#EEEEEE',
      'grey-300': '#E0E0E0',
      'grey-400': '#BDBDBD',
      'grey-500': '#9E9E9E',
      'grey-600': '#757575',
      'grey-700': '#616161',
      'grey-800': '#424242',
      'grey-900': '#212121',
      'perfect-scrollbar-thumb': '#dbdade',
      'skin-bordered-background': '#fff',
      'skin-bordered-surface': '#fff',
      'expansion-panel-text-custom-bg': '#fafafa',
      'track-bg': '#F0F2F8',
      'chat-bg': '#F7F6FA',
      'primary-100': '#D3EEEF',
      'primary-200': '#A7DDDF',
      'primary-300': '#7CCDCF',
      'primary-400': '#50BCBF',
      'primary-500': '#24ABAF',
      'primary-600': '#209A9D',
      'primary-700': '#1F9195',
      'primary-800': '#1D898C',
      'primary-900': '#1B8083',
      'paper': '#FFFFFF',
      'excel-header': '#217346',
      'excel-axis': '#E6E6E6',
      'excel-axis-text': '#252D3A',
      'text-primary': '#252D3A',
      'text-secondary': '#2E263D',
      'backdrop-overlay': '#252D3A',
      'access-denied-overlay': '#F6FAFA',
      'onboarding-backdrop': '#252D3A',
      'telegram': '#25A0DF',
      'max': '#9530D6',
      'vk': '#0073F7',
    },

    variables: {
      'code-color': '#d400ff',
      'overlay-scrim-background': '#252D3A',
      'tooltip-background': '#3F4953',
      'overlay-scrim-opacity': 0.65,
      'hover-opacity': 0.04,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'custom-opacity': 0.12,
      'dragged-opacity': 0.1,
      'disabled-opacity': 0.4,
      'border-color': '#2E263D',
      'border-opacity': 0.12,
      'table-header-color': '#F6F7FB',
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,
      'backdrop-overlay-opacity': 0.65,
      'onboarding-backdrop-opacity': 0.65,

      // 👉 shadows
      'shadow-key-umbra-color': '#131120',
      'shadow-xs-opacity': '0.16',
      'shadow-sm-opacity': '0.18',
      'shadow-md-opacity': '0.20',
      'shadow-lg-opacity': '0.22',
      'shadow-xl-opacity': '0.24',
    },
  },

  dark: {
    dark: true,
    colors: {
      'primary': staticPrimaryColor,
      'on-primary': '#fff',
      'primary-darken-1': staticPrimaryDarkenColor,
      'secondary': '#8A8D93',
      'secondary-darken-1': '#7C7F84',
      'on-secondary': '#fff',
      'success': '#65CD50',
      'success-darken-1': '#4DB600',
      'on-success': '#fff',
      'info': '#16B1FF',
      'info-darken-1': '#149FE6',
      'on-info': '#fff',
      'warning': '#FFB400',
      'warning-darken-1': '#E6A200',
      'on-warning': '#fff',
      'error': '#F35C5F',
      'error-darken-1': '#E64449',
      'on-error': '#fff',
      'background': '#1B2027',
      'on-background': '#E7E3FC',
      'surface': '#252B35',
      'on-surface': '#E9F2F8',
      'grey-50': '#303744',
      'grey-100': '#2F3349',
      'grey-200': '#4A5072',
      'grey-300': '#5E6692',
      'grey-400': '#7983BB',
      'grey-500': '#8692D0',
      'grey-600': '#AAB3DE',
      'grey-700': '#B6BEE3',
      'grey-800': '#CFD3EC',
      'grey-900': '#E7E9F6',
      'perfect-scrollbar-thumb': '#4a5072',
      'skin-bordered-background': '#312d4b',
      'skin-bordered-surface': '#312d4b',
      'expansion-panel-text-custom-bg': '#373350',
      'track-bg': '#474360',
      'chat-bg': '#373452',
      'primary-100': '#303744',
      'primary-200': '#A7DDDF',
      'primary-300': '#7CCDCF',
      'primary-400': '#50BCBF',
      'primary-500': '#24ABAF',
      'primary-600': '#209A9D',
      'primary-700': '#1F9195',
      'primary-800': '#1D898C',
      'primary-900': '#1B8083',
      'paper': '#252B35',
      'excel-header': '#217346',
      'excel-axis': '#E6E6E6',
      'excel-axis-text': '#252D3A',
      'text-primary': '#E9F2F8',
      'text-secondary': '#E7E3FC',
      'backdrop-overlay': '#3A4857',
      'access-denied-overlay': '#3F4755',
      'onboarding-backdrop': '#0E1116',
      'telegram': '#25A0DF',
      'max': '#9530D6',
      'vk': '#0073F7',
    },

    variables: {
      'code-color': '#d400ff',
      'overlay-scrim-background': '#3A4857',
      'tooltip-background': '#E8E8E9',
      'overlay-scrim-opacity': 0.5,
      'hover-opacity': 0.04,
      'focus-opacity': 0.1,
      'selected-opacity': 0.08,
      'activated-opacity': 0.16,
      'pressed-opacity': 0.14,
      'custom-opacity': 0.12,
      'disabled-opacity': 0.4,
      'dragged-opacity': 0.1,
      'border-color': '#E7E3FC',
      'border-opacity': 0.12,
      'table-header-color': '#3D3759',
      'high-emphasis-opacity': 0.9,
      'medium-emphasis-opacity': 0.7,
      'backdrop-overlay-opacity': 0.5,
      'onboarding-backdrop-opacity': 0.6,

      // 👉 Shadows
      'shadow-key-umbra-color': '#131120',
      'shadow-xs-opacity': '0.20',
      'shadow-sm-opacity': '0.22',
      'shadow-md-opacity': '0.24',
      'shadow-lg-opacity': '0.26',
      'shadow-xl-opacity': '0.28',
    },
  },
}

export default themes
